import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, useClerk } from '@clerk/react';
import {
  Container,
  Title,
  Text,
  Card,
  Badge,
  Stack,
  Group,
  Loader,
  Alert,
  Button,
  SimpleGrid,
  Box,
  Center,
  Divider,
} from '@mantine/core';
import {
  IconExclamationCircle,
  IconArrowLeft,
  IconLock,
  IconLockOpen,
  IconAlbum,
  IconPhoto,
} from '@tabler/icons-react';
import { useLiveQuery, useApiClient, useSession, mediaUrl } from '../api';
import { formatCents } from '../components/products/productDisplay';
import ProtectedMediaItem from '../components/media/ProtectedMediaItem';

// Priced album page (contract §3.15 AlbumPublic + §4.16).
//
// Locked (items === null): cover + details + "Unlock for $X" — the checkout
// POST returns a CCBill FlexForm URL and we leave the SPA (TipModal pattern);
// the webhook completes the purchase and the `album.unlocked` notification on
// user.{clerk_user_id}.notifications makes useLiveQuery refetch, flipping the
// page to unlocked when the buyer returns from checkout.
//
// Unlocked (items array): every item renders through the entitlement-checked
// seller-media file route (§5 — it also serves public items, one code path).
//
// `redirect` is injectable because jsdom's window.location.assign is
// LegacyUnforgeable (can't be spied/stubbed) — tests pass a spy.
const AlbumDetailPage = ({ redirect = (url) => window.location.assign(url) }) => {
  const { albumId } = useParams();
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const { openSignIn } = useClerk();
  const { me } = useSession();
  const api = useApiClient();

  const { data: album, loading, error, refetch } = useLiveQuery(
    `/api/v1/albums/${albumId}`,
    null,
    { topics: [me && `user.${me.clerk_user_id}.notifications`] },
  );

  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null);

  const handleUnlock = async () => {
    if (!isSignedIn) {
      openSignIn();
      return;
    }
    if (submitting) return;
    setNotice(null);
    setSubmitting(true);
    try {
      const { url } = await api.post(`/api/v1/albums/${albumId}/purchase-checkout`);
      redirect(url); // keep the button loading while the browser navigates away
    } catch (err) {
      if (err?.code === 'conflict') {
        // Already purchased (completed row exists) — refetch to unlock.
        await refetch();
      } else if (err?.code === 'config') {
        setNotice("Payments aren't configured yet — please try again later.");
      } else if (err?.code === 'auth') {
        openSignIn();
      } else {
        setNotice(err?.message || 'Could not start the checkout');
      }
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container size="lg" py="xl">
        <Center py="xl"><Loader size="lg" /></Center>
      </Container>
    );
  }

  if (error || !album) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
          {error?.message || 'Album not found'}
        </Alert>
      </Container>
    );
  }

  const unlocked = Array.isArray(album.items);

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size="1rem" />}
          onClick={() => navigate(`/princess/${album.seller_id}`)}
          style={{ alignSelf: 'flex-start' }}
        >
          Back to the princess
        </Button>

        <Card shadow="md" padding="xl" radius="lg" withBorder>
          <Stack gap="md">
            <Group gap="xs" wrap="nowrap">
              <IconAlbum size="1.4rem" color="var(--mantine-color-grape-5)" />
              <Title order={2} c="grape">{album.title}</Title>
              {unlocked || album.is_purchased ? (
                <Badge color="green" variant="light" leftSection={<IconLockOpen size="0.7rem" />}>
                  Unlocked
                </Badge>
              ) : (
                <Badge color="gray" variant="light" leftSection={<IconLock size="0.7rem" />}>
                  Locked
                </Badge>
              )}
            </Group>

            {album.description && <Text c="dimmed">{album.description}</Text>}

            <Group gap="lg">
              <Text size="xl" fw={700} c="pink">{formatCents(album.price_cents)}</Text>
              <Group gap={4}>
                <IconPhoto size="0.9rem" color="var(--mantine-color-gray-6)" />
                <Text size="sm" c="dimmed">
                  {album.item_count} item{album.item_count === 1 ? '' : 's'}
                </Text>
              </Group>
            </Group>

            {!unlocked && (
              <>
                {album.cover_path && (
                  <Box style={{ borderRadius: 10, overflow: 'hidden', maxHeight: 340 }}>
                    <img
                      src={mediaUrl(album.cover_path)}
                      alt={album.title}
                      style={{ width: '100%', height: '100%', maxHeight: 340, objectFit: 'cover', display: 'block' }}
                    />
                  </Box>
                )}

                {notice && (
                  <Alert icon={<IconExclamationCircle size="1rem" />} color="orange" variant="light">
                    {notice}
                  </Alert>
                )}

                <Divider />

                <Group justify="space-between" align="center" wrap="wrap" gap="md">
                  <Text size="sm" c="dimmed">
                    Unlock once, keep forever — this album stays yours after purchase.
                  </Text>
                  <Button
                    size="md"
                    color="pink"
                    variant="gradient"
                    gradient={{ from: 'pink', to: 'grape', deg: 45 }}
                    leftSection={<IconLock size="1rem" />}
                    onClick={handleUnlock}
                    loading={submitting}
                  >
                    Unlock for {formatCents(album.price_cents)}
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        </Card>

        {unlocked && (
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
            {album.items.map((item) => (
              <Box
                key={item.id}
                style={{ position: 'relative', height: 220, borderRadius: 10, overflow: 'hidden', background: '#111' }}
              >
                <ProtectedMediaItem
                  mediaId={item.id}
                  mediaType={item.media_type}
                  title={item.title}
                  alt={item.title || item.caption || 'Album item'}
                />
              </Box>
            ))}
          </SimpleGrid>
        )}
      </Stack>
    </Container>
  );
};

export default AlbumDetailPage;
